import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChatAlt2Icon, PlusIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import Pagination from '@/shared/Pagination'

import VoteButton from './VoteButton'
import { STATUS_LABELS, STATUS_COLORS, CATEGORY_LABELS, CATEGORY_COLORS, SORT_OPTIONS, formatDate } from './shared'

const STATUS_KEYS = ['under_review', 'planned', 'maybe', 'complete', 'closed']
const DEFAULT_STATUSES = ['under_review', 'planned', 'maybe']

const FeedbackIndex = () => {
  const { getRequest } = useQuery()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [category, setCategory] = useState('')
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES)
  const [sort, setSort] = useState('newest')
  const [page, setPage] = useState(1)

  const fetchData = () => {
    setLoading(true)
    const params = { page, sort }
    if (search) params.search = encodeURIComponent(search)
    if (category) params.category = category
    if (statuses.length > 0) params.status = statuses.join(',')
    getRequest('/api/v1/feedbacks', params, (err, json) => {
      setLoading(false)
      if (err) return
      setData(json)
    })
  }

  useEffect(() => { fetchData() }, [search, category, statuses.join(','), sort, page])

  // Debounce the search input.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const toggleStatus = (s) => {
    setPage(1)
    setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])
  }

  if (!data && loading) return <Loading />
  if (!data) return null

  return (
    <div className='py-8'>
      <div className='flex items-center justify-between gap-4 mb-6'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100'>Feedback</h1>
          <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Help shape Closing Credits. Suggest features, report bugs, vote on what matters.</p>
        </div>
        <Link
          to='/feedback/new'
          className='inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-clcpurple text-white text-sm font-semibold hover:bg-clcpurple/90'
        >
          <PlusIcon className='h-4 w-4' /> New
        </Link>
      </div>

      <div className='space-y-3'>
        <input
          type='text'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder='Search…'
          className='block w-full sm:max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg text-sm focus:ring-clcpurple focus:border-clcpurple'
        />

        <div className='flex flex-wrap items-center gap-2'>
          {STATUS_KEYS.map(s => {
            const active = statuses.includes(s)
            return (
              <button
                key={s}
                type='button'
                onClick={() => toggleStatus(s)}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active ? STATUS_COLORS[s] : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {STATUS_LABELS[s]}
              </button>
            )
          })}
        </div>

        <div className='flex flex-wrap items-center gap-2'>
          <button
            type='button'
            onClick={() => { setCategory(''); setPage(1) }}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold ${category === '' ? 'bg-clcpurple text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >
            All
          </button>
          <button
            type='button'
            onClick={() => { setCategory('feature_request'); setPage(1) }}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold ${category === 'feature_request' ? 'bg-clcpurple text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >
            Feature requests
          </button>
          <button
            type='button'
            onClick={() => { setCategory('bug_report'); setPage(1) }}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold ${category === 'bug_report' ? 'bg-clcpurple text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >
            Bug reports
          </button>

          <div className='ml-auto'>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1) }}
              className='px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-md text-xs font-semibold focus:ring-clcpurple focus:border-clcpurple'
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className='mt-5 bg-white dark:bg-gray-800 rounded-lg shadow divide-y divide-gray-100 dark:divide-gray-700'>
        {data.feedbacks.length === 0 ? (
          <div className='py-12 text-center text-sm text-gray-500'>No feedback matches these filters.</div>
        ) : (
          data.feedbacks.map(fb => (
            <Link
              key={fb.id}
              to={`/feedback/${fb.slug}`}
              className='block hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors'
            >
              <div className='px-4 py-3 flex items-start gap-4'>
                <div className='shrink-0' onClick={(e) => e.preventDefault()}>
                  <VoteButton slug={fb.slug} voted={fb.voted} votesCount={fb.votesCount} size='sm' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-semibold text-gray-900 dark:text-gray-100'>{fb.title}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${CATEGORY_COLORS[fb.category]}`}>{CATEGORY_LABELS[fb.category]}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[fb.status]}`}>{STATUS_LABELS[fb.status]}</span>
                  </div>
                  <div className='mt-1 flex items-center gap-3 text-xs text-gray-500'>
                    <span>{fb.user.displayName}</span>
                    <span>·</span>
                    <span>{formatDate(fb.createdAt)}</span>
                    {fb.commentsCount > 0 && (
                      <span className='inline-flex items-center gap-1'><ChatAlt2Icon className='h-3.5 w-3.5' />{fb.commentsCount}</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      <div className='mt-4'>
        <Pagination meta={data.meta} callback={(p) => setPage(p)} scrollTop />
      </div>
    </div>
  )
}

export default FeedbackIndex
